import { useContext, useEffect } from "react";
import { Button, Col, Empty, Input, Pagination, Rate, Row, Select } from "antd";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import filter from "../../../assets/svg/filter.svg";
import "../../../assets/css/reputation-manager.css";
import { useConnectPlatforms } from "../settings/hooks/useConnectPlatforms";
import { useReviews } from "./hooks/useReviews";
import Loader from "../../loader";
import ReviewCard from "./ReviewCard";
import { AuthContext } from "../../../context/AuthContext";
import { getUserInfo } from "../../../utils/utils";
import useUpgradeAlert from "../../../common/hooks/useUpgradeAlert";
import { subscriptionPlans, userRoles } from "../../../config";

const { Search } = Input;

export const OneStarDisplay = () => {
  return (
    <Rate disabled defaultValue={1} count={1} style={{ fontSize: "14px" }} />
  );
};

const Reviews = () => {
  const { reviews, loading } = useReviews();
  const { platforms } = useConnectPlatforms();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const page = searchParams.get("page");
  const { isRMView, subscription } = useContext(AuthContext);
  const { role } = getUserInfo();
  const showUpgradeAlert = useUpgradeAlert(() => navigate("/pricing"));

  useEffect(() => {
    if (
      role === userRoles?.admin &&
      [subscriptionPlans?.trail, subscriptionPlans?.starter].includes(
        subscription?.subscriptionPackage?.name
      )
    ) {
      showUpgradeAlert();
    } else if (!isRMView) {
      navigate("/dashboard-view");
    }
  }, [isRMView, navigate]);

  const onFilterChange = (key, val) => {
    if (val !== "all") {
      searchParams.set(key, val);
    } else {
      searchParams.delete(key);
    }
    navigate(`${location.pathname}?${searchParams.toString()}`);
  };

  return (
    <div className="reputation-manager p-20">
      <h1 className="heading24px fw-bold">Reviews</h1>
      <p className="heading16px mb40 text-color-848484 fw-normal">
        Manage and respond to guest reviews across all platforms.
      </p>
      <div className="platform-card mb15 p-20">
        <div className="heading20px fw-bold mb10">Filter Reviews</div>

        <Row gutter={[16, 16]}>
          <Col xs={24} md={24} lg={8}>
            <Search
              placeholder="Search reviews or authors..."
              allowClear
              value={searchParams.get("term")}
              onInput={(val) => onFilterChange("term", val.target.value)}
            />
          </Col>
          <Col xs={24} md={12} lg={4}>
            <Select
              className="w-100"
              placeholder="Source"
              value={searchParams.get("platformName")}
              options={[
                { label: "All Sources", value: "all" },
                ...platforms?.map((d) => ({
                  label: d?.name,
                  value: d?.platformName,
                })),
              ]}
              onChange={(val) => onFilterChange("platformName", val)}
            />
          </Col>
          <Col xs={24} md={12} lg={4}>
            <Select
              className="w-100"
              value={searchParams.get("rating")}
              placeholder="Rating"
              options={[
                { label: "All Ratings", value: "all" },
                {
                  label: (
                    <div className="d-flex align-items-center gap-10">
                      5 <OneStarDisplay />
                    </div>
                  ),
                  value: "5",
                },
                {
                  label: (
                    <div className="d-flex align-items-center gap-10">
                      4 <OneStarDisplay />
                    </div>
                  ),
                  value: "4",
                },
                {
                  label: (
                    <div className="d-flex align-items-center gap-10">
                      3 <OneStarDisplay />
                    </div>
                  ),
                  value: "3",
                },
                {
                  label: (
                    <div className="d-flex align-items-center gap-10">
                      2 <OneStarDisplay />
                    </div>
                  ),
                  value: "2",
                },
                {
                  label: (
                    <div className="d-flex align-items-center gap-10">
                      1 <OneStarDisplay />
                    </div>
                  ),
                  value: "1",
                },
              ]}
              onChange={(val) => onFilterChange("rating", val)}
            />
          </Col>
          <Col xs={24} md={12} lg={4}>
            <Select
              className="w-100"
              placeholder="Sentiment"
              value={searchParams.get("sentiment")}
              options={[
                { label: "All Sentiment", value: "all" },
                { label: "Positive", value: "POSITIVE" },
                { label: "Neutral", value: "NEUTRAL" },
                { label: "Negative", value: "NEGATIVE" },
              ]}
              onChange={(val) => onFilterChange("sentiment", val)}
            />
          </Col>
          <Col xs={24} md={12} lg={4}>
            <Select
              className="w-100"
              placeholder="Response Status"
              value={searchParams.get("responded")}
              options={[
                { label: "All", value: "all" },
                { label: "Responded", value: true },
                { label: "Unresponded", value: false },
              ]}
              onChange={(val) => onFilterChange("responded", val)}
            />
          </Col>
        </Row>
      </div>
      <div className="d-flex align-items-center justify-content-between mt20 mb15">
        <span className="heading18px fw-semibold">
          {reviews?.totalElements} Reviews
        </span>
        <Button
          className="fw-medium bg-transparent clear-filter"
          onClick={() => navigate(location.pathname, { replace: true })}
        >
          <img src={filter} alt="" style={{ width: 20, height: 20 }} />
          <span className="heading14px">Clear Filters</span>
        </Button>
      </div>
      {loading ? (
        <Loader />
      ) : (
        <>
          {!!reviews?.content?.length ? (
            <>
              <ReviewCard reviews={reviews} />
              <div className="mt30">
                <Pagination
                  defaultCurrent={page}
                  total={reviews.totalElements}
                  pageSize={reviews.size}
                  align="center"
                  hideOnSinglePage
                  onChange={(page, pageSize) => onFilterChange("page", page)}
                />
              </div>
            </>
          ) : (
            <div className="d-flex align-items-center mt40 justify-content-center">
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No reviews"
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Reviews;
